import React, { useContext, useEffect, useState, useRef } from "react";
import {
  X, Download, Upload, Database, Shield, AlertTriangle,
  CheckCircle, Loader2, FileArchive, ChevronDown, ChevronUp,
  Info, Lock
} from "lucide-react";
import { AuthContext } from "../App";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function BackupModal({ open, onClose }) {
  const { user, activeEmpresaPropia, empresasPropias, token } = useContext(AuthContext);

  // Permisos
  const isSuper    = user?.role === "super_admin" || user?.role === "admin";
  const isAdmin    = user?.role === "admin" || user?.role === "super_admin";
  const puedeExportar  = isAdmin || isSuper || (user?.permisos || []).includes("backup.db_exportar");
  const puedeRestaurar = isAdmin || isSuper || (user?.permisos || []).includes("backup.db_restaurar");

  // Estado empresa para backup nivel 1
  const logoDisponibles = React.useMemo(() => {
    if (!empresasPropias) return [];
    if (isAdmin || isSuper) return empresasPropias;
    const ids = user?.logos_asignados || [];
    return empresasPropias.filter(e => ids.includes(e.id));
  }, [empresasPropias, user, isAdmin, isSuper]);

  const [selectedLogo, setSelectedLogo] = useState(null);
  const [infoData, setInfoData] = useState(null);
  const [infoTotal, setInfoTotal] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [loadingInfoTotal, setLoadingInfoTotal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadingTotal, setDownloadingTotal] = useState(false);

  // Restaurar
  const [showRestore, setShowRestore] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [restoring, setRestoring] = useState(false);
  const fileInputRef = useRef(null);

  // Pre-seleccionar empresa activa
  useEffect(() => {
    if (open && activeEmpresaPropia && !selectedLogo) {
      setSelectedLogo(activeEmpresaPropia.slug);
    }
    if (!open) {
      setInfoData(null);
      setInfoTotal(null);
      setShowRestore(false);
      setConfirmText("");
    }
  }, [open, activeEmpresaPropia]);

  // Cargar info cuando cambia empresa seleccionada
  useEffect(() => {
    if (!open || !selectedLogo || !puedeExportar) return;
    setLoadingInfo(true);
    setInfoData(null);
    fetch(`${API}/api/admin/backup/empresa/info?logo_tipo=${selectedLogo}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => setInfoData(d))
      .catch(() => {})
      .finally(() => setLoadingInfo(false));
  }, [selectedLogo, open]);

  // Cargar info total (superadmin)
  useEffect(() => {
    if (!open || !isSuper) return;
    setLoadingInfoTotal(true);
    fetch(`${API}/api/admin/backup/total/info`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => setInfoTotal(d))
      .catch(() => {})
      .finally(() => setLoadingInfoTotal(false));
  }, [open, isSuper]);

  // ── Exportar empresa ──────────────────────────────────────────
  const exportarEmpresa = async () => {
    if (!selectedLogo) { toast.error("Seleccioná una empresa"); return; }
    setDownloading(true);
    try {
      const res = await fetch(`${API}/api/admin/backup/empresa/exportar?logo_tipo=${selectedLogo}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Error al exportar");
      }
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="([^"]+)"/);
      const fname = match ? match[1] : `backup_${selectedLogo}.arbackup`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup descargado exitosamente");
    } catch (e) {
      toast.error(e.message || "Error al exportar backup");
    } finally {
      setDownloading(false);
    }
  };

  // ── Exportar total ────────────────────────────────────────────
  const exportarTotal = async () => {
    setDownloadingTotal(true);
    try {
      const res = await fetch(`${API}/api/admin/backup/total/exportar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Error al exportar");
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="([^"]+)"/);
      const fname = match ? match[1] : "backup_total.zip";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup total descargado");
    } catch (e) {
      toast.error("Error al exportar backup total");
    } finally {
      setDownloadingTotal(false);
    }
  };

  // ── Restaurar empresa ─────────────────────────────────────────
  const restaurar = async (file) => {
    if (!selectedLogo) { toast.error("Seleccioná una empresa"); return; }
    if (confirmText !== "RESTAURAR") { toast.error('Escribí RESTAURAR para confirmar'); return; }
    setRestoring(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API}/api/admin/backup/empresa/restaurar?logo_tipo=${selectedLogo}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error al restaurar");
      toast.success("Datos restaurados exitosamente");
      setShowRestore(false);
      setConfirmText("");
      // Recargar info
      setInfoData(null);
    } catch (e) {
      toast.error(e.message || "Error al restaurar backup");
    } finally {
      setRestoring(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith(".arbackup")) {
      toast.error("Solo se aceptan archivos .arbackup");
      return;
    }
    restaurar(file);
    e.target.value = "";
  };

  if (!open) return null;

  const empresaActual = logoDisponibles.find(e => e.slug === selectedLogo);
  const fmtNum = (n) => (n || 0).toLocaleString("es-PY");

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-[#0f172a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-gradient-to-r from-blue-900/30 to-transparent">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-500/20 flex items-center justify-center">
              <Database className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-white font-semibold text-base">BackUp de mis datos</h2>
              <p className="text-slate-400 text-xs">Exportación y restauración de datos</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6 space-y-5">

          {/* ── NIVEL 1: Backup de empresa ─────────────── */}
          {(puedeExportar || puedeRestaurar) && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-4">
              {/* Título */}
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-400" />
                <span className="text-white font-medium text-sm">Backup de empresa</span>
                <span className="ml-auto text-xs bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded-full">
                  Encriptado
                </span>
              </div>
              <p className="text-slate-400 text-xs leading-relaxed">
                Exporta todos los datos de una empresa en un archivo <code className="text-blue-300">.arbackup</code> encriptado.
                Solo puede restaurarse dentro de este sistema — no es compatible con otros ERPs.
              </p>

              {/* Selector de empresa */}
              <div>
                <label className="text-xs text-slate-400 mb-1.5 block">Empresa</label>
                <select
                  value={selectedLogo || ""}
                  onChange={e => setSelectedLogo(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-500/50"
                >
                  <option value="">— Seleccionar empresa —</option>
                  {logoDisponibles.map(e => (
                    <option key={e.slug} value={e.slug}>{e.nombre}</option>
                  ))}
                </select>
              </div>

              {/* Info de la empresa */}
              {selectedLogo && (
                <div className="bg-black/20 rounded-lg px-4 py-3 text-xs text-slate-400">
                  {loadingInfo ? (
                    <div className="flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando información...</div>
                  ) : infoData ? (
                    <div className="flex items-center justify-between">
                      <span>Total documentos: <strong className="text-white">{fmtNum(infoData.total_documentos)}</strong></span>
                      <span className="text-slate-500">empresa: {infoData.logo_tipo}</span>
                    </div>
                  ) : null}
                </div>
              )}

              {/* Botón exportar */}
              {puedeExportar && (
                <button
                  onClick={exportarEmpresa}
                  disabled={downloading || !selectedLogo}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white text-sm font-medium transition-colors"
                >
                  {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  {downloading ? "Generando backup..." : "Descargar backup (.arbackup)"}
                </button>
              )}

              {/* Sección restaurar */}
              {puedeRestaurar && (
                <div className="border-t border-white/10 pt-4">
                  <button
                    onClick={() => setShowRestore(v => !v)}
                    className="flex items-center gap-2 text-xs text-amber-400 hover:text-amber-300 transition-colors w-full"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    <span>Restaurar desde archivo</span>
                    {showRestore ? <ChevronUp className="w-3.5 h-3.5 ml-auto" /> : <ChevronDown className="w-3.5 h-3.5 ml-auto" />}
                  </button>

                  {showRestore && (
                    <div className="mt-3 space-y-3">
                      {/* Advertencia */}
                      <div className="flex items-start gap-2 bg-red-900/20 border border-red-500/30 rounded-lg px-3 py-2.5">
                        <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                        <p className="text-xs text-red-300 leading-relaxed">
                          <strong>ADVERTENCIA:</strong> Esta acción elimina TODOS los datos actuales de la empresa
                          y los reemplaza con los del backup. Esta operación no se puede deshacer.
                        </p>
                      </div>
                      {/* Confirmación */}
                      <div>
                        <label className="text-xs text-slate-400 mb-1 block">
                          Escribí <strong className="text-white">RESTAURAR</strong> para habilitar la restauración
                        </label>
                        <input
                          type="text"
                          value={confirmText}
                          onChange={e => setConfirmText(e.target.value)}
                          placeholder="RESTAURAR"
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-red-500/50 font-mono"
                        />
                      </div>
                      {/* Botón subir archivo */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".arbackup"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={restoring || confirmText !== "RESTAURAR" || !selectedLogo}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-700 hover:bg-red-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-white text-sm font-medium transition-colors"
                      >
                        {restoring ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {restoring ? "Restaurando..." : "Seleccionar archivo .arbackup"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Sin permisos ───────────────────────────── */}
          {!puedeExportar && !puedeRestaurar && !isSuper && (
            <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-5">
              <Lock className="w-5 h-5 text-slate-500 shrink-0" />
              <p className="text-slate-400 text-sm">No tenés permisos para realizar backups. Contactá al administrador.</p>
            </div>
          )}

          {/* ── NIVEL 2: Backup total (superadmin) ─────── */}
          {isSuper && (
            <div className="bg-white/5 border border-amber-500/20 rounded-xl p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Database className="w-4 h-4 text-amber-400" />
                <span className="text-white font-medium text-sm">Backup total del sistema</span>
                <span className="ml-auto text-xs bg-amber-500/15 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded-full">
                  Solo superadmin
                </span>
              </div>
              <p className="text-slate-400 text-xs leading-relaxed">
                Exporta <strong className="text-white">toda la base de datos</strong> de todas las empresas.
                El ZIP incluye un script y las instrucciones para restaurar por comando en el servidor.
                <span className="text-amber-400"> No tiene restauración web.</span>
              </p>

              {/* Info total */}
              <div className="bg-black/20 rounded-lg px-4 py-3 text-xs text-slate-400">
                {loadingInfoTotal ? (
                  <div className="flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Cargando...</div>
                ) : infoTotal ? (
                  <span>Total en sistema: <strong className="text-white">{fmtNum(infoTotal.total_documentos)}</strong> documentos en todas las empresas</span>
                ) : null}
              </div>

              {/* Aviso */}
              <div className="flex items-start gap-2 bg-amber-900/20 border border-amber-500/20 rounded-lg px-3 py-2.5">
                <Info className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-200/80 leading-relaxed">
                  El archivo ZIP no está encriptado. Contiene datos de todas las empresas.
                  Guardarlo en un lugar seguro.
                </p>
              </div>

              <button
                onClick={exportarTotal}
                disabled={downloadingTotal}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white text-sm font-medium transition-colors"
              >
                {downloadingTotal ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileArchive className="w-4 h-4" />}
                {downloadingTotal ? "Generando ZIP..." : "Descargar backup total (.zip)"}
              </button>
            </div>
          )}

          {/* Nota pie */}
          <div className="flex items-start gap-2 text-xs text-slate-500">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              El archivo <code className="text-slate-400">.arbackup</code> está encriptado con AES-256 y solo puede restaurarse
              en este sistema por el administrador. Si perdés datos, mandanos el archivo y lo restauramos.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
